/**
 * Pricing API Service
 * 
 * Handles pricing tiers, subscriptions, usage tracking, and AI credits.
 * Uses cookie-based session management with credentials: 'include'.
 * 
 * @module pricingApi
 */

import { apiConfig } from '@/config/apiConfig';
import type {
  TierType,
  PricingTier,
  PricingTiersResponse,
  TierDetailsResponse,
  TierMetricsResponse,
  QuoteRequest,
  QuoteResponse,
  CompareRequest,
  CompareResponse,
  BLYNCalculateRequest,
  BLYNCalculateResponse,
  TokenPacksResponse,
  SignalStackCalculateRequest,
  SignalStackCalculateResponse,
  AIDecisioningCalculateRequest,
  AIDecisioningCalculateResponse,
  Subscription,
  CreateSubscriptionRequest,
  SubscriptionResponse,
  UpgradeRequest,
  UpgradePreviewResponse,
  EnableModuleRequest,
  DisableModuleRequest,
  ModulesResponse,
  PricingModulesResponse,
  CancelSubscriptionRequest,
  EnterpriseContactRequest,
  EnterpriseContactResponse,
  ConvertTrialRequest,
  TrackUsageRequest,
  TrackUsageResponse,
  BatchTrackUsageRequest,
  UsageSummaryRequest,
  UsageSummaryResponse,
  QuotaRequest,
  QuotaResponse,
  UsageAlertsRequest,
  UsageAlertsResponse,
  BillingSummaryRequest,
  BillingSummaryResponse,
  DeductOverageRequest,
  DeductOverageResponse,
  CheckBalanceRequest,
  CheckBalanceResponse,
  AssignCreditsRequest,
  AssignCreditsResponse,
  DeductCreditsRequest,
  DeductCreditsResponse,
  CreditBalanceRequest,
  CreditBalance,
  LedgerRequest,
  LedgerResponse,
  CreditsUsageSummaryRequest,
  CreditsUsageSummaryResponse,
  RateCardRequest,
  RateCardResponse,
  CalculateCreditsRequest,
  CalculateCreditsResponse,
  PriceFieldsResponse,
  CalculatePriceRequest,
  CalculatePriceResponse,
  DBUSAFieldsResponse,
  DBUSACalculateRequest,
  DBUSACalculateResponse,
} from '@/types/pricing';

const BASE_URL = apiConfig.baseUrls.pricing;
const endpoints = apiConfig.pricingEndpoints;

/**
 * Custom error class for Pricing API errors
 */
export class PricingApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public errorCode?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'PricingApiError';
  }
}

/**
 * Pricing-svc errors are FastAPI-style ({ detail: ... }), not { message: ... }.
 * Fall back through detail (string or validation-error array) before message.
 */
function errorMessageFromBody(body: any): string {
  const detail = body?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  if (typeof body?.message === 'string') return body.message;
  return 'Request failed';
}

/**
 * Base fetch function with credentials and error handling
 */
async function fetchWithCredentials<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401) {
    throw new PricingApiError('Session expired', 401, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new PricingApiError(
      errorMessageFromBody(error),
      response.status,
      error.error_code,
      error.details
    );
  }

  return response.json();
}

/**
 * Public fetch function (no auth required)
 */
async function fetchPublic<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new PricingApiError(
      errorMessageFromBody(error),
      response.status,
      error.error_code,
      error.details
    );
  }

  return response.json();
}

// ============================================
// Pricing Tiers (Public)
// ============================================

/**
 * Get all pricing tiers
 */
export async function getPricingTiers(): Promise<PricingTiersResponse> {
  return fetchPublic<PricingTiersResponse>(endpoints.getTiers, {
    method: 'GET',
  });
}

/**
 * Get all available add-on modules (public)
 */
export async function getPricingModules(): Promise<PricingModulesResponse> {
  return fetchPublic<PricingModulesResponse>(endpoints.getPricingModules, {
    method: 'GET',
  });
}

/**
 * Get tier details
 */
export async function getTierDetails(tier: TierType): Promise<TierDetailsResponse> {
  return fetchPublic<TierDetailsResponse>(`${endpoints.getTierDetails}/${tier}`, {
    method: 'GET',
  });
}

/**
 * Get tier metrics
 */
export async function getTierMetrics(tier: TierType): Promise<TierMetricsResponse> {
  return fetchPublic<TierMetricsResponse>(`${endpoints.getTierMetrics}/${tier}/metrics`, {
    method: 'GET',
  });
}

/**
 * QR campaign price from pricing-svc (`personalized_qr_code`, volume-tiered CPM).
 * The rate lives only in pricing-svc, so price changes need no frontend change.
 * Throws if pricing-svc can't price it; callers must not fall back to a local rate.
 */
export async function getQrCampaignPrice(
  quantity: number,
  tier: TierType
): Promise<{ totalPrice: number; pricePerQRCode: number }> {
  if (quantity <= 0) return { totalPrice: 0, pricePerQRCode: 0 };

  const res = await fetchPublic<{
    success: boolean;
    total: number;
    line_items?: Array<{ field: string; calculated_price: number }>;
  }>(endpoints.calculatePrice, {
    method: 'POST',
    body: JSON.stringify({
      fields: [{ field: 'personalized_qr_code', count: quantity }],
      tier,
    }),
  });

  const line = res.line_items?.find((l) => l.field === 'personalized_qr_code');
  if (!res.success || !line || !Number.isFinite(res.total)) {
    throw new PricingApiError('QR campaign price is unavailable', 0);
  }
  // Effective per-code price derived from the total, so it always agrees with
  // the total whatever unit or tier pricing-svc applied.
  const totalPrice = Math.round(res.total * 100) / 100;
  return { totalPrice, pricePerQRCode: totalPrice / quantity };
}

/** Maps subscription tier (or unknown string) to pricing API tier path segment. */
export function normalizePricingTierForApi(tier: string | undefined | null): TierType {
  const t = (tier || 'launch').toLowerCase();
  if (t === 'growth' || t === 'scale' || t === 'enterprise') return t;
  return 'launch';
}

/** Default when tier metrics omit predictive / model_build (matches common rate card). */
const DEFAULT_PREDICTIVE_MODEL_BUILD_USD = 375;

/**
 * Model creation checkout: use pricing API `metrics.predictive` → `model_build.overage_price_usd`.
 */
export function getPredictiveModelBuildFee(
  metrics: TierMetricsResponse | undefined
): { unitUsd: number; displayName: string } {
  const predictive = metrics?.metrics?.predictive;
  if (!Array.isArray(predictive)) {
    return { unitUsd: DEFAULT_PREDICTIVE_MODEL_BUILD_USD, displayName: 'Model Build Fee' };
  }
  const row = predictive.find((m) => m.metric_name === 'model_build');
  const raw = row?.overage_price_usd;
  const unitUsd =
    typeof raw === 'number' && Number.isFinite(raw) && raw >= 0
      ? raw
      : DEFAULT_PREDICTIVE_MODEL_BUILD_USD;
  return {
    unitUsd,
    displayName: (row?.display_name && String(row.display_name).trim()) || 'Model Build Fee',
  };
}

/**
 * Calculate pricing quote
 */
export async function getQuote(data: QuoteRequest): Promise<QuoteResponse> {
  return fetchPublic<QuoteResponse>(endpoints.getQuote, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Compare tiers
 */
export async function compareTiers(data: CompareRequest): Promise<CompareResponse> {
  return fetchPublic<CompareResponse>(endpoints.compareTiers, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// BLYN Tokens (Public)
// ============================================

/**
 * Calculate BLYN token usage
 */
export async function calculateBLYN(
  data: BLYNCalculateRequest
): Promise<BLYNCalculateResponse> {
  return fetchPublic<BLYNCalculateResponse>(endpoints.calculateBLYN, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get BLYN token packs
 */
export async function getBLYNPacks(): Promise<TokenPacksResponse> {
  return fetchPublic<TokenPacksResponse>(endpoints.getBLYNPacks, {
    method: 'GET',
  });
}

// ============================================
// SignalStack (Public)
// ============================================

/**
 * Calculate SignalStack usage
 */
export async function calculateSignalStack(
  data: SignalStackCalculateRequest
): Promise<SignalStackCalculateResponse> {
  return fetchPublic<SignalStackCalculateResponse>(endpoints.calculateSignalStack, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// AI Decisioning (Public)
// ============================================

/**
 * Calculate AI Decisioning usage
 */
export async function calculateAIDecisioning(
  data: AIDecisioningCalculateRequest
): Promise<AIDecisioningCalculateResponse> {
  return fetchPublic<AIDecisioningCalculateResponse>(endpoints.calculateAIDecisioning, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Subscription (Auth Required)
// ============================================

/**
 * Create subscription
 */
export async function createSubscription(
  data: CreateSubscriptionRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.createSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get subscription for user
 */
export async function getSubscription(userID: number): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.getSubscription, {
    method: 'POST',
    body: JSON.stringify({ userID }),
  });
}

/**
 * Upgrade/downgrade subscription
 */
export async function upgradeSubscription(
  data: UpgradeRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.upgradeSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Preview upgrade changes
 */
export async function previewUpgrade(
  data: UpgradeRequest
): Promise<UpgradePreviewResponse> {
  return fetchWithCredentials<UpgradePreviewResponse>(endpoints.upgradePreview, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Enable module
 */
export async function enableModule(data: EnableModuleRequest): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.enableModule, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Disable module
 */
export async function disableModule(
  data: DisableModuleRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.disableModule, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get enabled modules
 */
export async function getModules(userID: number): Promise<ModulesResponse> {
  return fetchWithCredentials<ModulesResponse>(endpoints.getModules, {
    method: 'POST',
    body: JSON.stringify({ userID }),
  });
}

/**
 * Cancel subscription
 */
export async function cancelSubscription(
  data: CancelSubscriptionRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.cancelSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Enterprise plan — notify sales (email to hey@boostt.ai when SMTP/SES is configured).
 */
export async function contactEnterprise(
  data: EnterpriseContactRequest
): Promise<EnterpriseContactResponse> {
  return fetchWithCredentials<EnterpriseContactResponse>(endpoints.contactEnterprise, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Convert trial to paid
 */
export async function convertTrial(data: ConvertTrialRequest): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.convertTrial, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Usage Tracking (Auth Required)
// ============================================

/**
 * Track usage event
 */
export async function trackUsage(data: TrackUsageRequest): Promise<TrackUsageResponse> {
  return fetchWithCredentials<TrackUsageResponse>(endpoints.trackUsage, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Batch track usage events
 */
export async function trackUsageBatch(
  data: BatchTrackUsageRequest
): Promise<{ success: boolean; message: string }> {
  return fetchWithCredentials(endpoints.trackUsageBatch, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get usage summary
 */
export async function getUsageSummary(
  data: UsageSummaryRequest
): Promise<UsageSummaryResponse> {
  return fetchWithCredentials<UsageSummaryResponse>(endpoints.getUsageSummary, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get quota status
 */
export async function getQuota(data: QuotaRequest): Promise<QuotaResponse> {
  return fetchWithCredentials<QuotaResponse>(endpoints.getQuota, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get usage alerts
 */
export async function getUsageAlerts(data: UsageAlertsRequest): Promise<UsageAlertsResponse> {
  return fetchWithCredentials<UsageAlertsResponse>(endpoints.getUsageAlerts, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get usage history for a metric
 */
export async function getUsageHistory(data: {
  userID: number;
  metric_name: string;
  months?: number;
}): Promise<{
  success: boolean;
  tenant_id: number;
  metric_name: string;
  history: Array<{ period: string; usage_quantity: number; overage_charge: number }>;
}> {
  return fetchWithCredentials(endpoints.getUsageHistory || '/api/usage/history', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get billing summary
 */
export async function getBillingSummary(
  data: BillingSummaryRequest
): Promise<BillingSummaryResponse> {
  return fetchWithCredentials<BillingSummaryResponse>(endpoints.getBillingSummary, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Deduct overage from wallet
 */
export async function deductOverage(
  data: DeductOverageRequest
): Promise<DeductOverageResponse> {
  return fetchWithCredentials<DeductOverageResponse>(endpoints.deductOverage, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Check wallet balance for overage
 */
export async function checkOverageBalance(
  data: CheckBalanceRequest
): Promise<CheckBalanceResponse> {
  return fetchWithCredentials<CheckBalanceResponse>(endpoints.checkOverageBalance, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// AI Credits (Auth Required)
// ============================================

/**
 * Assign credits to user
 */
export async function assignCredits(
  data: AssignCreditsRequest
): Promise<AssignCreditsResponse> {
  return fetchWithCredentials<AssignCreditsResponse>(endpoints.assignCredits, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Deduct credits
 */
export async function deductCredits(
  data: DeductCreditsRequest
): Promise<DeductCreditsResponse> {
  return fetchWithCredentials<DeductCreditsResponse>(endpoints.deductCredits, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get credit balance
 */
export async function getCreditBalance(userID: number): Promise<CreditBalance> {
  const data: CreditBalanceRequest = { userID };
  return fetchWithCredentials<CreditBalance>(endpoints.getCreditBalance, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get credit ledger (transaction history)
 */
export async function getCreditLedger(data: LedgerRequest): Promise<LedgerResponse> {
  return fetchWithCredentials<LedgerResponse>(endpoints.getCreditLedger, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get credits usage summary
 */
export async function getCreditsUsageSummary(
  data: CreditsUsageSummaryRequest
): Promise<CreditsUsageSummaryResponse> {
  return fetchWithCredentials<CreditsUsageSummaryResponse>(endpoints.getCreditsUsageSummary, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get rate card
 */
export async function getRateCard(data?: RateCardRequest): Promise<RateCardResponse> {
  if (data) {
    return fetchWithCredentials<RateCardResponse>(endpoints.getRateCard, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
  return fetchPublic<RateCardResponse>(endpoints.getRateCard, {
    method: 'GET',
  });
}

/**
 * Calculate credits (preview)
 */
export async function calculateCredits(
  data: CalculateCreditsRequest
): Promise<CalculateCreditsResponse> {
  return fetchWithCredentials<CalculateCreditsResponse>(endpoints.calculateCredits, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Price Calculator (Public)
// ============================================

/**
 * Get all pricing fields
 */
export async function getPriceFields(): Promise<PriceFieldsResponse> {
  return fetchPublic<PriceFieldsResponse>(endpoints.getPriceFields, {
    method: 'GET',
  });
}

/**
 * Calculate price
 */
export async function calculatePrice(
  data: CalculatePriceRequest
): Promise<CalculatePriceResponse> {
  return fetchPublic<CalculatePriceResponse>(endpoints.calculatePrice, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get DB USA fields
 */
export async function getDBUSAFields(): Promise<DBUSAFieldsResponse> {
  return fetchPublic<DBUSAFieldsResponse>(endpoints.getDBUSAFields, {
    method: 'GET',
  });
}

/**
 * Calculate DB USA price
 */
export async function calculateDBUSA(
  data: DBUSACalculateRequest
): Promise<DBUSACalculateResponse> {
  return fetchPublic<DBUSACalculateResponse>(endpoints.calculateDBUSA, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Account (Sub-Tenant) Subscriptions
// ============================================

export interface AccountSubscriptionRequest {
  account_id: string;
  userID: number;
}

export interface CreateAccountSubscriptionRequest extends AccountSubscriptionRequest {
  tier?: string;
  billing_email?: string;
  start_trial?: boolean;
  trial_days?: number;
}

export interface UpgradeAccountSubscriptionRequest extends AccountSubscriptionRequest {
  tier: string;
}

export interface CancelAccountSubscriptionRequest extends AccountSubscriptionRequest {
  reason?: string;
}

export async function createAccountSubscription(
  data: CreateAccountSubscriptionRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.createAccountSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAccountSubscription(
  data: AccountSubscriptionRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.getAccountSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAccountSubscriptionOverview(
  data: AccountSubscriptionRequest
): Promise<{ success: boolean; subscription: any; subscription_found: boolean; tiers: any[] }> {
  return fetchWithCredentials(endpoints.getAccountSubscriptionOverview, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function upgradeAccountSubscription(
  data: UpgradeAccountSubscriptionRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.upgradeAccountSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function cancelAccountSubscription(
  data: CancelAccountSubscriptionRequest
): Promise<SubscriptionResponse> {
  return fetchWithCredentials<SubscriptionResponse>(endpoints.cancelAccountSubscription, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// API Class
// ============================================

/**
 * Pricing API class for advanced usage
 */
export class PricingAPI {
  // Pricing Tiers
  getPricingTiers = getPricingTiers;
  getTierDetails = getTierDetails;
  getTierMetrics = getTierMetrics;
  getQuote = getQuote;
  compareTiers = compareTiers;
  
  // BLYN
  calculateBLYN = calculateBLYN;
  getBLYNPacks = getBLYNPacks;
  
  // SignalStack
  calculateSignalStack = calculateSignalStack;
  
  // AI Decisioning
  calculateAIDecisioning = calculateAIDecisioning;
  
  // Subscription
  createSubscription = createSubscription;
  getSubscription = getSubscription;
  upgradeSubscription = upgradeSubscription;
  previewUpgrade = previewUpgrade;
  enableModule = enableModule;
  disableModule = disableModule;
  getModules = getModules;
  cancelSubscription = cancelSubscription;
  contactEnterprise = contactEnterprise;
  convertTrial = convertTrial;
  
  // Usage Tracking
  trackUsage = trackUsage;
  trackUsageBatch = trackUsageBatch;
  getUsageSummary = getUsageSummary;
  getQuota = getQuota;
  getUsageAlerts = getUsageAlerts;
  getBillingSummary = getBillingSummary;
  deductOverage = deductOverage;
  checkOverageBalance = checkOverageBalance;
  
  // AI Credits
  assignCredits = assignCredits;
  deductCredits = deductCredits;
  getCreditBalance = getCreditBalance;
  getCreditLedger = getCreditLedger;
  getCreditsUsageSummary = getCreditsUsageSummary;
  getRateCard = getRateCard;
  calculateCredits = calculateCredits;
  
  // Price Calculator
  getPriceFields = getPriceFields;
  calculatePrice = calculatePrice;
  getDBUSAFields = getDBUSAFields;
  calculateDBUSA = calculateDBUSA;

  // Account (Sub-Tenant) Subscriptions
  createAccountSubscription = createAccountSubscription;
  getAccountSubscription = getAccountSubscription;
  upgradeAccountSubscription = upgradeAccountSubscription;
  cancelAccountSubscription = cancelAccountSubscription;
}

export const pricingApi = new PricingAPI();
export default pricingApi;

